import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, MoreHorizontal, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/frontend/components/ui/button";
import { SidebarTrigger } from "@/frontend/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/frontend/components/ui/dropdown-menu";
import { getInitials, formatDateTime } from "@/frontend/utils/shared";
import { PageShell } from "../PageShell";
import { PatientStatusBadge } from "../patients/PatientStatusBadge";
import { DeletePatientDialog } from "./DeletePatientDialog";
import { useWorkspacePersona } from "../individual/useWorkspacePersona";
import {
  usePatientRetrievalStatusQuery,
  usePatientSummaryQuery,
} from "@/frontend/queries/dashboard/patients";
import { useTriggerPatientRetrievalMutation } from "@/frontend/mutations/dashboard/patients";

/**
 * Header shared by every patient section, carrying identity, the lifecycle badge
 * and the two primary actions, with destructive actions in the overflow menu. In
 * an individual workspace the reader is the patient, so the invite badge and the
 * org phrasing drop away while fetch records and delete stay.
 */
export function PatientPageHeader({ patientId }: { patientId: string }) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { isIndividual } = useWorkspacePersona();
  const { data: summary } = usePatientSummaryQuery(patientId);
  const { data: retrievalStatus } = usePatientRetrievalStatusQuery(patientId);
  const triggerRetrieval = useTriggerPatientRetrievalMutation();

  const isSyncing = retrievalStatus?.isActive || triggerRetrieval.isPending;
  const hasSources = (summary?.row.healthSystemCount ?? 0) > 0;

  const meta = summary
    ? [
        summary.communicationEmail,
        `added ${formatDateTime(summary.row.createdAt)}`,
      ].filter(Boolean)
    : [];

  return (
    <div className="shrink-0 border-b">
      <PageShell className="py-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <SidebarTrigger className="-ml-2 shrink-0 text-muted-foreground" />
            {summary ? (
              <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-violet-100 text-caption font-semibold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                <span data-patient>{getInitials(summary.row.name, summary.communicationEmail)}</span>
              </span>
            ) : (
              <span className="size-12 shrink-0 animate-pulse rounded-full bg-muted" />
            )}
            <div className="min-w-0">
              {summary ? (
                <>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="truncate text-xl font-semibold tracking-tight" data-patient>
                      {summary.row.name}
                    </h1>
                    {!isIndividual && (
                      <PatientStatusBadge
                        status={summary.row.status}
                        needsAttention={summary.row.needsAttention}
                      />
                    )}
                  </div>
                  <p className="mt-1.5 truncate text-caption text-muted-foreground" data-patient>
                    {meta.join(" · ")}
                  </p>
                </>
              ) : (
                <>
                  <span className="block h-7 w-52 animate-pulse rounded bg-muted" />
                  <span className="mt-1.5 block h-3.5 w-72 animate-pulse rounded bg-muted" />
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              className="h-9 gap-1.5"
              disabled={!hasSources || isSyncing}
              title={!hasSources ? "Connect a health system first" : undefined}
              onClick={() =>
                triggerRetrieval.mutate(
                  { patientMembershipId: patientId },
                  {
                    onSuccess: (data) => {
                      if (data.scheduledCount > 0) {
                        toast.success(
                          `Fetching records from ${data.scheduledCount} ${data.scheduledCount === 1 ? "source" : "sources"}`,
                        );
                      } else if (data.alreadyActiveCount > 0) {
                        toast.info("Records are already being fetched");
                      } else if (data.errorCount > 0) {
                        toast.error("Could not start a fetch");
                      } else {
                        toast.info("Nothing to fetch yet");
                      }
                    },
                    onError: () => toast.error("Could not start a fetch"),
                  },
                )
              }
            >
              {isSyncing ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              {isSyncing ? "Fetching…" : "Fetch records"}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="size-9" aria-label="More actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: "/dashboard/patients/$patientId/onboarding",
                      params: { patientId },
                    })
                  }
                >
                  {isIndividual ? "Connect health systems" : "Manage health systems"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={() => setConfirmDelete(true)}>
                  <Trash2 />
                  {isIndividual ? "Delete my records" : "Delete patient"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </PageShell>

      <DeletePatientDialog
        patientMembershipId={patientId}
        patientName={summary?.row.name}
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
      />
    </div>
  );
}
